/**
 * A work's consignment from the owner's or the agent's side: what the panel
 * says about the one that stands, and whether a typed offer is fit to send.
 *
 * Pure. The fetched row and the draft go in; a panel view or a request body
 * comes out. The copy is `consign.constants.ts`; the read, the two writes,
 * the eligibility check and the form's open/sending state are
 * `useConsignController.ts`. The gallery's reading of the same row is
 * `gallery/gallery.model.ts`, which this borrows a date format from.
 *
 * `toOfferRequest` refuses a floor above the asking price before the round
 * trip, although Postgres would take it — a gallery reading the pair would
 * assume they were swapped. And `canAct` decides only the sentence under an
 * absence; whether the control is drawn is the view's call on the same flag.
 */

import type {
  ConsignmentArrangement,
  ConsignmentBasis,
  OfferConsignmentRequest,
  WorkConsignment,
} from "@ondo/shared";
import { formatMoney } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { parseMoneyMinor } from "../lib/money.ts";
import { formatConsignedDate } from "../gallery/gallery.model.ts";
import { CONSIGN_COPY } from "./consign.constants.ts";

/** The typed form, as the fields hold it. Strings until submit. */
export interface OfferDraft {
  ondoId: string;
  arrangement: ConsignmentArrangement;
  basis: ConsignmentBasis;
  currency: string;
  asking: string;
  floor: string;
  note: string;
}

export const EMPTY_OFFER: OfferDraft = {
  ondoId: "",
  arrangement: "consignment",
  basis: "unspecified",
  currency: "GBP",
  asking: "",
  floor: "",
  note: "",
};

/** The on-blur answer about the gallery id, in the shape the agent modal uses. */
export type GalleryCheck =
  | { state: "checking" }
  | { state: "eligible"; displayName: string }
  | { state: "refused"; message: string }
  | null;

/** The current consignment, with every label decided; or the absence. */
export interface ConsignmentSummary {
  consignmentId: string;
  /** 'Offered to Hauser & Wirth (ONDO-6624) on 04 Feb 2026 — awaiting their answer'. */
  headline: string;
  accepted: boolean;
  /** Label/value pairs for the terms panel. Figures absent are omitted. */
  terms: { label: string; value: string }[];
  /** The owner's location switch — OND-88 — and the sentence that explains its state. */
  location: { shared: boolean; note: string };
}

/**
 * The sentence under the location switch, decided from two facts: whether it
 * is on, and whether the gallery has yet accepted. A ticked box on an
 * unanswered offer discloses nothing until acceptance, and the owner is told
 * so rather than left to assume the gallery can already see.
 */
export function describeLocationSharing(shared: boolean, accepted: boolean): string {
  if (!shared) return CONSIGN_COPY.location.off;
  return accepted ? CONSIGN_COPY.location.on : CONSIGN_COPY.location.pending;
}

export type ConsignPanelView =
  | { kind: "loading"; message: string }
  | { kind: "none"; message: string; detail: string | null }
  | { kind: "held"; summary: ConsignmentSummary };

/**
 * What the panel shows, decided here.
 *
 * `canAct` decides only the SENTENCE under an absence — whether the reader is
 * told how to offer, or simply that nothing is out. Whether the control is
 * drawn is the view's business, gated on the same flag.
 */
export function toConsignPanelView(
  consignment: WorkConsignment | null | undefined,
  canAct: boolean,
): ConsignPanelView {
  if (consignment === undefined) return { kind: "loading", message: CONSIGN_COPY.loading };
  if (consignment === null) {
    return canAct
      ? { kind: "none", message: CONSIGN_COPY.none, detail: CONSIGN_COPY.noneDetail }
      : { kind: "none", message: CONSIGN_COPY.noneReadOnly, detail: null };
  }

  return { kind: "held", summary: summarise(consignment) };
}

export function summarise(c: WorkConsignment): ConsignmentSummary {
  const accepted = c.acceptedAt !== null;
  const party = `${c.galleryName} (${c.galleryOndoId})`;
  const headline = accepted
    ? `${CONSIGN_COPY.consignedTo} ${party} ${CONSIGN_COPY.since} ${formatConsignedDate(c.acceptedAt ?? c.offeredAt)}`
    : `${CONSIGN_COPY.offeredTo} ${party} ${CONSIGN_COPY.on} ${formatConsignedDate(c.offeredAt)} — ${CONSIGN_COPY.awaiting}`;

  const terms: ConsignmentSummary["terms"] = [
    { label: CONSIGN_COPY.terms.arrangement, value: CONSIGN_COPY.arrangement[c.arrangement] },
    { label: CONSIGN_COPY.terms.basis, value: CONSIGN_COPY.basis[c.basis] },
  ];
  if (c.askingMinor !== null && c.currency) {
    terms.push({ label: CONSIGN_COPY.terms.asking, value: formatMoney({ minor: c.askingMinor, currency: c.currency }) });
  }
  if (c.floorMinor !== null && c.currency) {
    terms.push({ label: CONSIGN_COPY.terms.floor, value: formatMoney({ minor: c.floorMinor, currency: c.currency }) });
  }
  if (c.note) terms.push({ label: CONSIGN_COPY.terms.note, value: c.note });

  return {
    consignmentId: c.consignmentId,
    headline,
    accepted,
    terms,
    location: {
      shared: c.locationShared,
      note: describeLocationSharing(c.locationShared, accepted),
    },
  };
}

/** The ONDO id shape the server validates. Checked here so a typo costs no request. */
export function isOndoIdShape(typed: string): boolean {
  return /^ONDO-[0-9]{4,}$/i.test(typed.trim());
}

/**
 * The draft as the server takes it, or the one problem that stops it.
 *
 * Figures are parsed here and never sent as text — the schema takes minor
 * units. A floor above the asking price is refused before the round trip:
 * the database would accept it, and a gallery reading "asking £100k, floor
 * £150k" would reasonably assume the two were swapped.
 */
export function toOfferRequest(
  draft: OfferDraft,
): { ok: true; body: OfferConsignmentRequest } | { ok: false; problem: string } {
  if (!isOndoIdShape(draft.ondoId)) return { ok: false, problem: CONSIGN_COPY.errors.invalidId };

  const asking = draft.asking.trim() === "" ? null : parseMoneyMinor(draft.asking);
  const floor = draft.floor.trim() === "" ? null : parseMoneyMinor(draft.floor);
  if (draft.asking.trim() !== "" && asking === null) {
    return { ok: false, problem: CONSIGN_COPY.errors.invalidMoney };
  }
  if (draft.floor.trim() !== "" && floor === null) {
    return { ok: false, problem: CONSIGN_COPY.errors.invalidMoney };
  }
  if (asking !== null && floor !== null && floor > asking) {
    return { ok: false, problem: CONSIGN_COPY.errors.floorAboveAsking };
  }
  const currency = draft.currency.trim().toUpperCase();
  if ((asking !== null || floor !== null) && !/^[A-Z]{3}$/.test(currency)) {
    return { ok: false, problem: CONSIGN_COPY.errors.currencyNeeded };
  }

  return {
    ok: true,
    body: {
      ondoId: draft.ondoId.trim().toUpperCase(),
      arrangement: draft.arrangement,
      basis: draft.basis,
      askingMinor: asking,
      floorMinor: floor,
      currency: asking !== null || floor !== null ? currency : null,
      note: draft.note.trim() || null,
    },
  };
}

export function describeConsignError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.status === 429) return CONSIGN_COPY.errors.throttled;
    // The server's own sentence on a refusal, never one written here: it is
    // the same string the eligibility check would have given on blur.
    if (cause.message) return cause.message;
    if (cause.status === 403) return CONSIGN_COPY.errors.refused;
  }

  return CONSIGN_COPY.errors.refused;
}
